<template lang="pug">
    section.article-main-tab(:class="{ 'pt-2': activeTranslation }")
        //- A single-article product owns all shared data: the article form keeps only
        //- what belongs to the article (api docs/features/PRODUCT_ARTICLE_INHERITANCE.md)
        SingleArticleNote(v-if="isSingleArticleProduct && !activeTranslation" :product-id="product.id")
        .flex-row
            el-form-item.grow(v-if="!isSingleArticleProduct" :label="$t('Name')" prop="name")
                .translation-origin(
                    v-if="activeTranslation && form.translationOrigin.name"
                    :class="{ visible: showTranslationOrigin.includes('name') }"
                )
                    button.language(type="button" @click="toggleTranslationOriginVisibility('name')")
                        span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                        span(v-else) {{ $t('Show original text') }}
                        span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
                    transition(name="show-translation")
                        .translation-origin-content(
                            v-if="showTranslationOrigin.includes('name')"
                            v-html="form.translationOrigin.name"
                        )
                el-input(v-model="name" :placeholder="namePlaceholder" maxlength="255" show-word-limit)
                InheritedValueHint(v-if="!activeTranslation" :own="!!name" @reset="name = ''")
            el-form-item(v-if="!activeTranslation" :label="$t('Status')" prop="status")
                el-select.is-full-width(placeholder="" v-model="status")
                    el-option(v-for="(item, key) in options.status.items" :key="key" :label="$t(item.label)" :value="item.value")
        .flex-row(v-if="!activeTranslation")
            el-form-item.grow(:label="$t('Sku')" prop="sku")
                el-input(v-model="sku" maxlength="255" show-word-limit)
            el-form-item(:label="$t('GTIN')" prop="gtin")
                el-input(v-model="gtin" maxlength="14" show-word-limit :placeholder="$t('EAN-13, UPC-12, ITF-14')")
            //- a single-article product's shipping points live on the product
            el-form-item(
                v-if="product.type !== 'coupon' && !isSingleArticleProduct"
                :label="$t('Additional Shipping Points')"
                prop="shipping_points"
            )
                el-input-number(
                    v-model="shippingPoints"
                    :min="0"
                    controls-position="right"
                )
            el-form-item(
                v-if="product.type !== 'coupon'"
                :label="$t('Restock date')"
                prop="restock_date"
            )
                el-date-picker(
                    v-model="restockDate"
                    type="date"
                    :format="$t('yyyy-MM-dd')"
                    prefix-icon="far el-icon-fa-calendar-alt"
                    :clearable="true"
                )

        el-row.no-margin(v-if="!activeTranslation && !isSingleArticleProduct" :gutter="20")
            el-col(:sm="12")
                el-form-item(:label="$t('Condition')" prop="condition")
                    el-select.is-full-width(v-model="condition" clearable :placeholder="$t('Inherit from product')")
                        el-option(v-for="(item, key) in options.condition.items" :key="key" :label="$t(item.label)" :value="item.value")
                    InheritedValueHint(:own="!!condition" @reset="condition = null")
            el-col(:sm="12")
                el-form-item(:label="$t('Brand')" prop="brand")
                    el-input(v-model="brand" maxlength="70" show-word-limit :placeholder="brandPlaceholder")
                    InheritedValueHint(:own="!!brand" @reset="brand = null")

        .flex-row(v-if="product.type === 'coupon' && form.coupon")
            el-form-item.grow(:label="$t('Coupon amount')" prop="coupon.amount")
                .flex-row
                    transition(name="slide-left")
                        //- el-input.grow(
                        //-     v-if="couponAmountType === 'fixed'"
                        //-     type="number"
                        //-     ref="couponAmount"
                        //-     v-model="couponAmount"
                        //- )
                        el-input-number(
                            v-if="couponAmountType === 'fixed'"
                            v-model="couponAmount"
                            controls-position="right"
                            :min="0.01"
                            :step="0.01"
                            :step-strictly="true"
                            ref="couponAmount"
                        )
                    el-select.is-full-width(placeholder="" v-model="couponAmountType")
                        el-option(
                            :label="$t('automatically, based on the price')"
                            value="auto"
                        )
                        el-option(
                            :label="$t('fixed amount')"
                            value="fixed"
                        )
            el-form-item(label=" " prop="coupon.discard_remaining_amount")
                el-switch(
                    :active-text="$t('Discard the remaining amount')"
                    v-model="discardRemainingAmount"
                )
            el-form-item(label=" " prop="coupon.discard_the_following_coupons")
                el-switch(
                    :active-text="$t('Discard the following coupons')"
                    v-model="discardTheFollowingCoupons"
                )

        el-form-item(v-if="!isSingleArticleProduct" :label="$t('Description')")
            //- el-input(v-model="description" type="textarea" :autosize="{ minRows: 3, maxRows: 40 }")
            .translation-origin(
                v-if="activeTranslation && form.translationOrigin.description"
                :class="{ visible: showTranslationOrigin.includes('description') }"
            )
                button.language(type="button" @click="toggleTranslationOriginVisibility('description')")
                    span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                    span(v-else) {{ $t('Show original text') }}
                    span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
                transition(name="show-translation")
                    .translation-origin-content.ql-editor(
                        v-if="showTranslationOrigin.includes('description')"
                        v-html="form.translationOrigin.description"
                    )
            quill-editor(v-model="description" :options="quillConfig")
            InheritedValueHint(v-if="!activeTranslation" :own="!!description" @reset="description = null")

        el-row.no-margin(v-if="product.type !== 'coupon' && !isSingleArticleProduct" :gutter="20")
            el-col.flex-row(:sm="24")
                el-form-item.grow(:label="$t('Unit')" prop="unit")
                    .translation-origin(
                        v-if="activeTranslation && form.translationOrigin.unit"
                        :class="{ visible: showTranslationOrigin.includes('unit') }"
                    )
                        button.language(type="button" @click="toggleTranslationOriginVisibility('unit')")
                            span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                            span(v-else) {{ $t('Show original text') }}
                            span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
                        transition(name="show-translation")
                            .translation-origin-content(
                                v-if="showTranslationOrigin.includes('unit')"
                                v-html="form.translationOrigin.unit"
                            )
                    el-input(
                        v-model="unit"
                        maxlength="255"
                        show-word-limit
                        :placeholder="unitPlaceholder || $t('I.e. Gramm, Liter, Bottle, etc.')"
                    )
                    InheritedValueHint(v-if="!activeTranslation" :own="!!unit" @reset="unit = null")
                el-form-item(v-if="!activeTranslation" :label="$t('Unit Quantity')" prop="quantity")
                    el-input-number(
                        v-model="quantity"
                        controls-position="right"
                        :min="0.01"
                        :step="0.01"
                        :step-strictly="true"
                        :placeholder="quantityPlaceholder"
                    )
                    InheritedValueHint(:own="quantity !== null && quantity !== undefined" @reset="quantity = null")

        el-form-item(v-if="!isSingleArticleProduct" :label="$t('Media')" prop="media")
            draggable(
                v-if="media"
                v-model="media"
                draggable="li.media"
            )
                transition-group(name="fade" tag="ul" class="media no-style-list product-media__grid")
                    ArticleMedium(
                        v-for="(media, index) in media"
                        :key="media.id"
                        :media="media"
                        :form="form"
                        :image-edit-enabled="hasImageEditFeature"
                        @edit-image="openEditImage"
                    )
                    li.media-generating(
                        v-for="upload in uploadsInFlight"
                        :key="`__upload_${upload.uid}`"
                    )
                        el-progress(type="circle" :percentage="upload.percent" :width="84" color="#409EFF")
                        span.media-generating__label {{ upload.name }}
                    li.media-generating(v-if="videoGen.active" key="__media_generating")
                        el-progress(type="circle" :percentage="videoGen.progress || 0" :width="84" color="#6E26FF")
                        span.media-generating__label {{ $t('Generating AI video…') }}
                    li.media-add(v-if="!activeTranslation" key="__media_add")
                        el-upload.plus-box(
                            ref="upload"
                            drag multiple :action="uploadEndpoint"
                            accept="image/jpg, image/jpeg, image/png, video/mp4"
                            :show-file-list="false"
                            :before-upload="validateUpload"
                            :on-change="updateUploadHeaders"
                            :on-progress="uploadProgress"
                            :on-success="uploadSucceed"
                            :on-error="uploadFailed"
                            :headers="uploadHeaders"
                            :data="uploadData"
                        )
                            i.el-icon-plus
                            .el-upload__text(v-html="$t('upload.drop.plural')")
                            //- Integrated into the dropzone: opens the library instead of
                            //- the file picker (stop the dragger's click → file dialog).
                            button.media-add__library(
                                type="button"
                                @click.stop.prevent="showLibraryDialog = true"
                            )
                                i.el-icon-picture-outline
                                span {{ $t('Select from library') }}
                            .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png/mp4', size: '12mb' }) }}
            .media-foot(v-if="media && media.length > 1")
                span.media-sort-hint
                    i.el-icon-d-caret
                    | {{ $t('Drag and drop to reorder files') }}
            .media-actions(v-if="!activeTranslation && hasVideoFeature")
                el-button(
                    type="primary"
                    icon="el-icon-fa-wand-magic-sparkles fas"
                    :disabled="!hasImages"
                    @click="showVideoDrawer = true"
                ) {{ $t('Generate AI Video') }}

            //- Single pick, matching the product gallery and the content image fields.
            image-library-dialog(
                :visible.sync="showLibraryDialog"
                :multiple="false"
                selection-type="media"
                @select="onArticleLibrarySelected"
            )

            GenerateVideoDrawer(
                v-if="hasVideoFeature"
                :visible.sync="showVideoDrawer"
                :product="form"
                subject-type="article"
                @generation-started="onVideoGenerationStarted"
                @completed="onGenerationCompleted"
            )
            GenerateImageEditDrawer(
                :visible.sync="showImageDrawer"
                :product="form"
                subject-type="article"
                :source-image-id="imageEditSourceId"
                :upload-context="imageEditorUploadContext"
                @completed="onGenerationCompleted"
                @updated="onImageUpdated"
                @replaced="onImageReplaced"
                @update-image-alt="onImageAltUpdated"
                @update-image-ai-disclosure="onImageAiDisclosureUpdated"
                @update-image-ai-label-display="onImageAiLabelDisplayUpdated"
                @update-image-ai-source-type="onImageAiSourceTypeUpdated"
            )
            InheritedValueHint(v-if="!activeTranslation" :own="!!(media && media.length)" @reset="media = []")

        el-form-item(
            v-if="!activeTranslation"
            :label="$t('Related Articles')"
        )
            RelatedArticlesField(
                v-model="relatedArticles"
                :shop-id="activeCms.shop.id"
                :disabled="!!activeTranslation"
            )

        .indexed-at-info(v-if="!activeTranslation && indexedAt")
            span.label {{ $t('Last indexed at') }}:
            span.value {{ indexedAt }}
        .indexed-at-info.pending(v-if="!activeTranslation && indexDirtySince")
            span.label {{ $t('Indexing pending since') }}:
            span.value {{ indexDirtySince }}
</template>

<i18n>
{
    "de": {
        "Sku": "Sku",
        "GTIN": "GTIN",
        "EAN-13, UPC-12, ITF-14": "EAN-13, UPC-12, ITF-14",
        "Condition": "Zustand",
        "New": "Neu",
        "Refurbished": "Generalüberholt",
        "Used": "Gebraucht",
        "Inherit from product": "Vom Produkt übernehmen",
        "Inherit from product: {brand}": "Vom Produkt übernommen: {brand}",
        "Brand": "Marke",
        "Coupon amount": "Gutscheinbetrag",
        "Discard the following coupons": "Nachfolgende Gutscheine verwerfen",
        "Discard the remaining amount": "Restbetrag verwerfen",
        "fixed amount": "Fester Betrag",
        "automatically, based on the price": "Automatisch, basierend auf dem Preis",
        "Unit": "Einzeleinheit Name",
        "I.e. Gramm, Liter, Bottle, etc.": "z.B. Gramm, Liter, Flasche, etc.",
        "Unit Quantity": "Menge der Einzeleinheit",
        "Additional Shipping Points": "Zusätzliche Versandpunkte",
        "Restock date": "Dispotermin",
        "Media": "Medien",
        "Generate AI Video": "KI-Video generieren",
        "Generating AI video…": "KI-Video wird generiert…",
        "Select from library": "Aus Mediathek wählen",
        "Drag and drop to reorder files": "Dateien per Drag and Drop sortieren",
        "Related Articles": "Verknüpfte Artikel",
        "Last indexed at": "Zuletzt indiziert am",
        "Indexing pending since": "Indizierung ausstehend seit"
    },
    "en": {
        "Inherit from product: {brand}": "Inherit from product: {brand}"
    }
}
</i18n>

<script>
// TODO: extend from @/extensions/shop/views/Product/Form/Main.vue

import _ from 'lodash';
import { fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone';

import { language, languageFlag, languageNativeName, formatDate } from '@/filters'
import { ensureFreshToken } from '@/helpers'
import { parseISO } from 'date-fns'

import pinia from '@/pinia';
import { useAuthStore, useSetupStore, useShopArticleStore, useShopProductStore } from '@/stores';
import draggable from 'vuedraggable';
import uniqid from 'uniqid';

import quillConfig from '@/quill/config';
import { withTextGenerationContext } from '@/quill/toolbar/textGeneration';

import ArticleMedium from '@/extensions/shop/views/Product/Form/Partials/ArticleMedium';
import InheritedValueHint from '../InheritedValueHint';
import SingleArticleNote from '../../SingleArticleNote';
import RelatedArticlesField from '@/extensions/shop/components/RelatedArticlesField';
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog';
import GenerateVideoDrawer from '@/extensions/generative-media/views/Drawers/GenerateVideoDrawer';
import GenerateImageEditDrawer from '@/extensions/generative-media/views/Drawers/GenerateImageEditDrawer';
import Echo from '@/echo';

export default {
    name: 'ArticleMainTab',
    data() {
        quillConfig.modules.link.pages = this.getPageOptions;
        quillConfig.bounds = 'body';
        return {
            // mainPriceType: 'main',
            quillConfig: withTextGenerationContext(quillConfig, () => ({ type: 'article', id: this.form.id, title: this.form.name })),
            uploadData: {
                'context[type]': 'article'
            },
            showTranslationOrigin: [],
            // Generative media (same flow as products, article-scoped).
            showVideoDrawer: false,
            showImageDrawer: false,
            showLibraryDialog: false,
            imageEditSourceId: null,
            videoGen: { active: false, progress: 0 },
            // Files currently uploading, keyed by el-upload's uid → percent. The dropzone
            // hides Element's own file list, so without this an upload showed nothing.
            uploads: {},
            articleTaskChannelName: null
        };
    },
    components: {
        InheritedValueHint,
        SingleArticleNote,
        ArticleMedium,
        draggable,
        RelatedArticlesField,
        ImageLibraryDialog,
        GenerateVideoDrawer,
        GenerateImageEditDrawer
    },
    watch: {
        // Article-level AI-task subscription: so a generation's result lands in the
        // gallery even when the drawer is closed ("you can close this and come back").
        id: {
            immediate: true,
            handler(value) {
                this.subscribeArticleTaskChannel(value);
            }
        },
        // The drawer shares the article channel and LEAVES it on close (dropping our
        // listeners). Re-subscribe after it closes so a generation that finishes while
        // the drawer is closed still refreshes the gallery ("close and come back").
        showImageDrawer(open) {
            if (!open) {
                this.$nextTick(() => this.subscribeArticleTaskChannel(this.id));
            }
        }
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        // Stable order so tiles do not jump around as percentages tick up.
        uploadsInFlight() {
            return _.sortBy(Object.values(this.uploads), 'uid');
        },
        id() {
            return _.get(this.form, 'id');
        },
        sku: {
            get() {
                return _.get(this.form, 'sku', '');
            },
            set(value) {
                this.setSku(value);
            }
        },
        gtin: {
            get() {
                return _.get(this.form, 'gtin', '');
            },
            set(value) {
                this.setGtin(value);
            }
        },
        condition: {
            get() {
                return _.get(this.form, 'condition', null);
            },
            set(value) {
                this.setCondition(value);
            }
        },
        brand: {
            get() {
                return _.get(this.form, 'brand', '');
            },
            set(value) {
                this.setBrand(value);
            }
        },
        brandPlaceholder() {
            const productBrand = _.get(this.product, 'brand');
            if (productBrand) return this.$t('Inherit from product: {brand}', { brand: productBrand });
            return this.$t('Inherit from product');
        },
        status: {
            get() {
                return _.get(this.form, 'status') || 'enabled';
            },
            set(value) {
                this.setStatus(value);
            }
        },
        name: {
            get() {
                return _.get(this.form, 'name', '');
            },
            set(value) {
                this.setName(value);
            }
        },
        namePlaceholder() {
            return _.get(this.product, 'name', '');
        },
        description: {
            get() {
                return _.get(this.form, 'description', '');
            },
            set(value) {
                this.setDescription(value);
            }
        },
        unit: {
            get() {
                return _.get(this.form, 'unit');
            },
            set(value) {
                this.setUnit(value);
            }
        },
        unitPlaceholder() {
            return _.get(this.product, 'unit');
        },
        quantity: {
            get() {
                const quantity = _.get(this.form, 'quantity');
                return quantity == null ? undefined : quantity;
            },
            set(value) {
                if (value === 0) value = undefined;
                this.setQuantity(value);
            }
        },
        quantityPlaceholder() {
            const placeholder = _.get(this.product, 'quantity');
            if (placeholder) return placeholder.toString()
        },
        shippingPoints: {
            get() {
                return _.get(this.form, 'shipping_points') || undefined;
            },
            set(value) {
                if (!value) value = null;
                this.setShippingPoints(value);
            }
        },
        restockDate: {
            get() {
                const timestamp = _.get(this.form, 'restock_date');
                return timestamp ? startOfDay(appFromUnixTime(timestamp)) : null;
            },
            set(value) {
                const timestamp = value ? appGetUnixTime(startOfDay(value)) : null;
                this.setRestockDate(timestamp);
            }
        },
        media: {
            get() {
                let media = _.cloneDeep(this.form.media) || [];
                media = media.sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0));
                return media;
            },
            set(media) {
                this.sortMedia(media);
            }
        },
        relatedArticles: {
            get() {
                return _.get(this.form, 'relatedArticles') || [];
            },
            set(articles) {
                if (!this.activeTranslation) this.setRelatedArticles(articles);
            }
        },
        couponAmountType: {
            get() {
                let type = 'auto'
                if (this.form.coupon && this.form.coupon.amount != null) type = 'fixed'
                return type
            },
            set(value) {
                if (this.form.coupon) {
                    if (value === 'auto') {
                        this.setCouponAmount(null)
                    } else {
                        let price = 0
                        if (this.form.price) price = this.form.price.value
                        else if (this.product.price) price = this.product.price.value
                        this.setCouponAmount(price)
                        setTimeout(() => this.$refs.couponAmount.focus(), 100)
                    }
                }
            }
        },
        couponAmount: {
            get() {
                return _.get(this.form, 'coupon.amount')
            },
            set(value) {
                if (this.form.coupon) {
                    if (value != null) value = parseFloat(value)
                    this.setCouponAmount(value)
                }
            }
        },
        discardTheFollowingCoupons: {
            get() {
                return _.get(this.form, 'coupon.discard_the_following_coupons') || false
            },
            set(value) {
                if (this.form.coupon) {
                    this.setDiscardTheFollowingCoupons(value)
                }
            }
        },
        discardRemainingAmount: {
            get() {
                return _.get(this.form, 'coupon.discard_remaining_amount') || false
            },
            set(value) {
                if (this.form.coupon) {
                    this.setDiscardRemainingAmount(value)
                }
            }
        },
        languageOptions() {
            return _.get(this.activeCms, 'languages') || []
        },
        defaultLanguage() {
            return this.languageOptions.find(language => language.default)
        },
        activeTranslation() {
            if (this.form.translationOrigin) return this.form
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        shopArticleStore() {
            return useShopArticleStore(pinia);
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
        form() {
            return this.shopArticleStore.form;
        },
        product() {
            return this.shopProductStore.form;
        },
        // a single-article product owns all shared data: its article form shows none of it
        isSingleArticleProduct() {
            return this.shopProductStore.isSingleArticleProduct;
        },
        options() {
            return this.shopArticleStore.options;
        },
        uploadEndpoint() {
            return this.setupStore.uploadEndpoint;
        },
        locale() {
            return this.setupStore.locale;
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        hasVideoFeature() {
            return !!(_.get(this.activeCms, 'extensions') || []).find(extension => extension.key === 'product-video');
        },
        hasImageEditFeature() {
            return !!(_.get(this.activeCms, 'extensions') || []).find(extension => extension.key === 'ai-image-edit');
        },
        hasImages() {
            return (this.media || []).some(item => item.type === 'image');
        },
        // Lets the image editor's own Replace/Upload control post to the article
        // upload endpoint with the article media context.
        imageEditorUploadContext() {
            return { endpoint: this.uploadEndpoint, type: 'article' };
        },
        indexedAt() {
            var value = _.get(this.form, 'indexed_at');
            if (!value) return null;
            return formatDate(parseISO(value), this.$t('yyyy-MM-dd') + ', HH:mm:ss');
        },
        indexDirtySince() {
            var value = _.get(this.form, 'index_dirty_since');
            if (!value) return null;
            return formatDate(parseISO(value), this.$t('yyyy-MM-dd') + ', HH:mm:ss');
        }
    },
    methods: {
        // After an AI generation completes, refetch the article so the new media
        // (video/image) appears in the gallery without a manual reload.
        onGenerationCompleted() {
            const id = _.get(this.form, 'id');
            if (id) {
                this.shopArticleStore.fetchArticle({ id, translation: false });
            }
        },
        // Subscribe to the article's generative-media channel at the FORM level (not just
        // the drawer) so a completed AI edit refreshes the gallery even if the drawer was
        // closed mid-generation.
        subscribeArticleTaskChannel(articleId) {
            this.unsubscribeArticleTaskChannel();
            if (!articleId) return;
            if (!Echo.client) Echo.initialize();
            this.articleTaskChannelName = `generative-media.article.${articleId}`;
            const channel = Echo.client.private(this.articleTaskChannelName);
            // Track an in-progress generation so the media grid shows a placeholder tile —
            // this lives on the form (not the drawer), so it survives the drawer closing.
            channel.listen('.task:progress', (payload) => {
                this.videoGen = { active: true, progress: _.get(payload, 'progress', 0) || 0 };
            });
            channel.listen('.task:completed', () => {
                this.videoGen = { active: false, progress: 0 };
                this.onGenerationCompleted();
            });
            channel.listen('.task:failed', () => {
                this.videoGen = { active: false, progress: 0 };
                this.onGenerationCompleted();
            });
        },
        // The video drawer dispatched a generation — show the placeholder right away,
        // before the first progress broadcast arrives.
        onVideoGenerationStarted() {
            this.videoGen = { active: true, progress: 0 };
        },
        unsubscribeArticleTaskChannel() {
            if (this.articleTaskChannelName && Echo.client) {
                Echo.client.leave(this.articleTaskChannelName);
                this.articleTaskChannelName = null;
            }
        },
        // The editor emitted an updated image. For an UNSAVED upload (client `new:` id,
        // possibly promoted to a real id via `previousId`), merge the change into the
        // form media in place — a refetch would wipe the unsaved upload. For a persisted
        // image, refetch to pick up the server-side mutation.
        onImageUpdated(image, previousId = null) {
            if (!image) return;
            const matchId = previousId != null ? previousId : _.get(image, 'id');
            const media = (_.get(this.form, 'media') || []).find(item => String(_.get(item, 'image.id')) === String(matchId));
            const wasUnsaved = String(matchId || '').startsWith('new:') || String(_.get(image, 'id') || '').startsWith('new:');
            if (media && media.image && wasUnsaved) {
                const keys = ['id', 'source', 'settings', 'processing_query_string', 'processedFile', 'activeVariant', 'variants', 'alt', 'width', 'height', 'url', 'upload', 'ai_disclosure', 'ai_source_type', 'ai_label_display', 'ai_assessment'];
                keys.forEach(key => this.$set(media.image, key, _.get(image, key)));
                return;
            }
            this.onGenerationCompleted();
        },
        // The editor replaced the image (uploaded a new file or picked one from the
        // library). Swap the whole image object in the matching media slot so the
        // article Save persists the new source.
        onImageReplaced(image, previousId) {
            if (!image) return;
            const media = (_.get(this.form, 'media') || []).find(item => String(_.get(item, 'image.id')) === String(previousId));
            if (media) {
                this.$set(media, 'image', image);
            }
        },
        // Sync an alt text edited in the editor drawer back into the article form so
        // the next article Save persists it (alt is part of the article media payload).
        onImageAltUpdated(imageId, alt) {
            const media = (_.get(this.form, 'media') || []).find(item => String(_.get(item, 'image.id')) === String(imageId));
            if (media && media.image) {
                this.$set(media.image, 'alt', alt);
            }
        },
        // Art. 50(4) disclosure decisions ride the product payload exactly like alt.
        onImageAiDisclosureUpdated(imageId, disclose) {
            const media = (_.get(this.form, 'media') || []).find(item => String(_.get(item, 'image.id')) === String(imageId));
            if (media && media.image) {
                this.$set(media.image, 'ai_disclosure', disclose === true);
            }
        },
        onImageAiLabelDisplayUpdated(imageId, mode) {
            const media = (_.get(this.form, 'media') || []).find(item => String(_.get(item, 'image.id')) === String(imageId));
            if (media && media.image) {
                this.$set(media.image, 'ai_label_display', mode || null);
            }
        },
        onImageAiSourceTypeUpdated(imageId, sourceType) {
            const media = (_.get(this.form, 'media') || []).find(item => String(_.get(item, 'image.id')) === String(imageId));
            if (media && media.image) {
                this.$set(media.image, 'ai_source_type', sourceType || null);
            }
        },
        // Opened from a specific image's "Edit" button — the unified Edit drawer
        // (crop + AI + layer stack) references that image directly.
        openEditImage(imageId) {
            this.imageEditSourceId = imageId;
            this.showImageDrawer = true;
        },
        toggleTranslationOriginVisibility(key) {
            if (this.showTranslationOrigin.includes(key)) this.showTranslationOrigin.splice(this.showTranslationOrigin.indexOf(key), 1);
            else this.showTranslationOrigin.push(key);
        },
        getLanguageName(code) {
            return language(code, this.locale ? this.locale.value : 'de')
        },
        getLanguageFlag(code) {
            return languageFlag(code)
        },
        getLanguageNativeName(code) {
            return languageNativeName(code)
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken();
        },
        // el-upload reports progress per file; the dropzone hides its built-in list, so
        // these drive a tile of our own for each file in flight.
        uploadProgress(event, file) {
            this.$set(this.uploads, file.uid, {
                uid: file.uid,
                name: file.name,
                percent: Math.round(_.get(event, 'percent', 0)),
            });
        },
        clearUploadProgress(file) {
            const uid = _.get(file, 'uid');
            if (uid !== undefined) this.$delete(this.uploads, uid);
        },
        async validateUpload(file) {
            await this.updateUploadHeaders();
            if (file.size / 1024 / 1024 > 12) {
                // this.$message.error('Avatar picture size can not exceed 2MB!');
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: '12mb' }),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject();
            }
        },
        async uploadSucceed(response, data) {
            this.clearUploadProgress(data);

            const imageMimeTypes = ['image/jpeg', 'image/gif', 'image/png', 'image/webp'];
            const videoMimeTypes = ['video/mp4', 'video/mpeg', 'video/webm', 'video/quicktime'];

            let type = 'file';
            if (imageMimeTypes.includes(response.mime)) type = 'image';
            else if (videoMimeTypes.includes(response.mime)) type = 'video';

            let image, video, file;
            switch (type) {
                case 'file':
                    file = {
                        id: response.id,
                        name: response.name,
                        mime: response.mime,
                        type: 'original',
                        upload: true
                    };
                    break;

                case 'image':
                    image = {
                        id: uniqid('new:'),
                        width: response.dimensions.width,
                        height: response.dimensions.height,
                        source: {
                            id: response.id,
                            name: response.name,
                            mime: response.mime,
                            type: 'original',
                            upload: true
                        }
                    };
                    break;

                case 'video':
                    video = {
                        id: uniqid('new:'),
                        width: response.dimensions.width,
                        height: response.dimensions.height,
                        source: {
                            id: response.id,
                            name: response.name,
                            mime: response.mime,
                            type: 'original',
                            upload: true
                        }
                    };
                    break;
            }

            const media = {
                id: uniqid('new:'),
                type,
                scope: 'article',
                position: this.form.media.length + 1,
                settings: {
                    show: {
                        list: !this.form.media.filter(el => el.type === 'image').length && type === 'image',
                        detail: true,
                        highlight: !this.form.media.filter(el => el.type === 'image').length && type === 'image',
                        disabled: false
                    }
                },
                image,
                video,
                file
            };

            setTimeout(() => {
                this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== data.uid);
            }, 1000);

            // this.form.media.push(media);

            this.addMedia(media);

            await this.updateUploadHeaders();
        },
        async uploadFailed(err, file) {
            this.clearUploadProgress(file);

            await this.updateUploadHeaders();

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            });
        },
        setSku(value) {
            this.shopArticleStore.setFormSku(value);
        },
        setGtin(value) {
            this.shopArticleStore.setFormGtin(value);
        },
        setCondition(value) {
            this.shopArticleStore.setFormCondition(value);
        },
        setBrand(value) {
            this.shopArticleStore.setFormBrand(value);
        },
        setStatus(value) {
            this.shopArticleStore.setFormStatus(value);
        },
        setName(value) {
            this.shopArticleStore.setFormName(value);
        },
        setDescription(value) {
            this.shopArticleStore.setFormDescription(value);
        },
        setUnit(value) {
            this.shopArticleStore.setFormUnit(value);
        },
        setQuantity(value) {
            this.shopArticleStore.setFormQuantity(value);
        },
        setShippingPoints(value) {
            this.shopArticleStore.setFormShippingPoints(value);
        },
        setRestockDate(value) {
            this.shopArticleStore.setFormRestockDate(value);
        },
        setCouponAmount(value) {
            this.shopArticleStore.setFormCouponAmount(value);
        },
        setDiscardTheFollowingCoupons(value) {
            this.shopArticleStore.setFormDiscardTheFollowingCoupons(value);
        },
        setDiscardRemainingAmount(value) {
            this.shopArticleStore.setFormDiscardRemainingAmount(value);
        },
        addMedia(media) {
            this.shopArticleStore.addFormMedia(media);
        },
        // Add one or more images chosen from the media library as new article media —
        // the upload alternative. The library payload (new:file-library: + source) is
        // persisted by the article backend's library-selection branch on Save.
        onArticleLibrarySelected(selection) {
            const picked = Array.isArray(selection) ? selection : [selection];
            picked.filter(Boolean).forEach((entry) => {
                // The picker offers images and videos, and says which it handed back.
                const kind = _.get(entry, 'kind') === 'video' ? 'video' : 'image';
                const payload = _.get(entry, 'payload', entry);
                const hasImage = (this.form.media || []).some(item => item.type === 'image');
                // Only an image can be the list/highlight picture, so a video never
                // claims those slots.
                const showsAsCover = kind === 'image' && !hasImage;

                const media = {
                    id: uniqid('new:'),
                    type: kind,
                    scope: 'article',
                    position: (this.form.media || []).length + 1,
                    settings: { show: { list: showsAsCover, detail: true, highlight: showsAsCover, disabled: false } },
                };

                if (kind === 'video') media.video = payload;
                else media.image = { ...payload, type: 'product-image' };

                this.addMedia(media);
            });
        },
        sortMedia(media) {
            this.shopArticleStore.sortFormMedia(media);
        },
        setRelatedArticles(articles) {
            this.shopArticleStore.setFormRelatedArticles(articles);
        }
    },
    beforeDestroy() {
        this.unsubscribeArticleTaskChannel();
    }
};
</script>

<style lang="sass" scoped>
// ===== Article media manager =====
// Gallery + dropzone in one flex-wrap row. The dropzone is the last item; it grows into
// the row's free space (wrapping to its own full-width row when the tiles fill the line).
// Tiles keep their natural 150px height (align-items: flex-start) so the dropzone's hint
// can sit beneath it without stretching the tiles.
::v-deep .product-media__grid
    display: flex
    flex-wrap: wrap
    align-items: flex-start
    gap: 8px
    margin: 0
    padding: 0
    // Neutralise ArticleMedium's float/margins so the flex row aligns cleanly.
    > li
        float: none
        margin: 0

// The dropzone tile — same .plus-box visual as everywhere. It GROWS into the row's free
// space and the dragger is pinned to the 150px tile height (a fixed height, not 100%,
// which would collapse when the dropzone wraps onto its own row). The validation hint and
// the integrated "select from library" action sit with it.
::v-deep .media-add
    flex: 1 1 260px
    min-width: 220px
    .el-upload
        display: block
        width: 100%
    .el-upload-dragger
        width: 100%
        height: 150px
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center
        gap: 8px
    // Combined affordance: a library shortcut inside the dropzone. Opens the library
    // dialog (the @click.stop in the template keeps the file picker from firing).
    .media-add__library
        display: inline-flex
        align-items: center
        gap: 6px
        padding: 4px 12px
        border: 1px solid #dcdfe6
        border-radius: 4px
        background: #fff
        font-size: 12px
        line-height: 1.2
        color: #6E26FF
        cursor: pointer
        transition: border-color 0.15s ease, background-color 0.15s ease
        &:hover
            border-color: #6E26FF
            background-color: rgba(110, 38, 255, 0.06)
    // The validation hint, directly beneath the dropzone.
    .el-upload__tip
        margin-top: 6px
        font-size: 12px
        line-height: 1.4
        color: #c0c4cc

.media-sort-hint
    display: inline-flex
    align-items: center
    gap: 6px
    font-size: 12px
    line-height: 1.4
    color: #6b7280

// Secondary creation action (AI video) grouped below.
.media-actions
    display: flex
    align-items: center
    margin-top: 12px

// In-progress AI video placeholder — a 150px tile matching the gallery, with a circular
// progress and a label. Lives in the grid so it's visible whether or not the drawer is open.
::v-deep .media-generating
    width: 150px
    height: 150px
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 10px
    border: 1px dashed #6E26FF
    border-radius: 5px
    background: rgba(110, 38, 255, 0.04)
    .media-generating__label
        font-size: 12px
        line-height: 1.2
        text-align: center
        color: #6E26FF
        padding: 0 8px

.indexed-at-info
    margin-top: 20px
    font-size: 12px
    color: #909399
    .label
        font-weight: 500
    .value
        margin-left: 4px
    &.pending
        margin-top: 4px
        color: #e6a23c
</style>
